/**
 * DynamicFilterBar — inline search + quick filter chips for fields
 * configured with showInFilters.
 */
import { Search, X } from 'lucide-react';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import type { DynamicField, DynamicTableFilterValue } from '../../types/dynamicEntity';

interface DynamicFilterBarProps {
  columns: DynamicField[];
  filters: DynamicTableFilterValue[];
  onFiltersChange: (filters: DynamicTableFilterValue[]) => void;
  /** Simple text search across all text-visible fields */
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

function updateFilter(
  filters: DynamicTableFilterValue[],
  fieldKey: string,
  value: unknown,
  valueTo?: unknown,
): DynamicTableFilterValue[] {
  const existing = filters.filter((f) => f.fieldKey !== fieldKey);
  if (value === '' || value == null) return existing;
  return [...existing, { fieldKey, value, valueTo }];
}

function getFilterValue(filters: DynamicTableFilterValue[], fieldKey: string): unknown {
  return filters.find((f) => f.fieldKey === fieldKey)?.value ?? '';
}

export function DynamicFilterBar({
  columns,
  filters,
  onFiltersChange,
  searchValue = '',
  onSearchChange,
}: DynamicFilterBarProps) {
  const filterableColumns = columns.filter((c) => c.displayConfig?.showInFilters);

  const activeCount = filters.length + (searchValue ? 1 : 0);

  return (
    <div className="flex items-center gap-2 flex-wrap px-3 py-2 border-b border-border bg-muted/20">
      {/* Global search */}
      {onSearchChange && (
        <div className="relative flex-shrink-0 w-48">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          <Input
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search…"
            className="h-7 pl-7 text-[11px]"
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      )}

      {/* Quick filters per field */}
      {filterableColumns.map((col) => {
        const filterType = col.displayConfig?.filterType ?? 'text';
        const currentVal = getFilterValue(filters, col.internalName);

        if (filterType === 'select' || filterType === 'status') {
          return (
            <Select
              key={col.fieldId}
              value={String(currentVal || '__all__')}
              onValueChange={(v) =>
                onFiltersChange(updateFilter(filters, col.internalName, v === '__all__' ? null : v || null))
              }
            >
              <SelectTrigger className="h-7 text-[11px] w-36">
                <SelectValue placeholder={col.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="text-[11px]">
                  All {col.label}
                </SelectItem>
                {col.options?.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="text-[11px]">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        }

        if (filterType === 'boolean') {
          return (
            <Select
              key={col.fieldId}
              value={String(currentVal || '__all__')}
              onValueChange={(v) =>
                onFiltersChange(updateFilter(filters, col.internalName, v === '__all__' ? null : v || null))
              }
            >
              <SelectTrigger className="h-7 text-[11px] w-28">
                <SelectValue placeholder={col.label} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__" className="text-[11px]">All</SelectItem>
                <SelectItem value="true" className="text-[11px]">Yes</SelectItem>
                <SelectItem value="false" className="text-[11px]">No</SelectItem>
              </SelectContent>
            </Select>
          );
        }

        // text / default
        return (
          <div key={col.fieldId} className="relative flex-shrink-0 w-36">
            <Input
              value={String(currentVal || '')}
              onChange={(e) =>
                onFiltersChange(updateFilter(filters, col.internalName, e.target.value || null))
              }
              placeholder={col.label}
              className="h-7 text-[11px]"
            />
            {!!currentVal && (
              <button
                type="button"
                onClick={() => onFiltersChange(updateFilter(filters, col.internalName, null))}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        );
      })}

      {/* Clear all */}
      {activeCount > 0 && (
        <button
          type="button"
          onClick={() => {
            onFiltersChange([]);
            onSearchChange?.('');
          }}
          className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive"
        >
          <X className="w-3 h-3" />
          Clear ({activeCount})
        </button>
      )}
    </div>
  );
}
